
import { computed, defineComponent, onBeforeMount, reactive, ref, Ref, watch } from 'vue'
import { useRoute } from 'vue-router'

// @ui-kit
import NewInput from '@/ui-kit/NewInput/index.vue'
import NewSelect from '@/ui-kit/NewSelect/index.vue'

// @components
// import NotificationsBadge from '@/modules/NotificationsModule/components/NotificationsBadge/index.vue'
import HeaderSearchSuggestions from '@/modules/NewDispatcher/components/HeaderSearchSuggestions/index.vue'
import HeaderProfile from '@/modules/NewDispatcher/components/HeaderProfile/index.vue'
import HeaderProfileDropdown from '@/modules/NewDispatcher/components/HeaderProfileDropdown/index.vue'

import NewButton from '@/ui-kit/NewButton/index.vue'

// @types
import { DispatcherSector } from '@/new-services/types/dispatcher-sectors'
import { GlobalSearchT } from '@/core/types/global_search.types'

// @realisations
import dispatcherSectorsAPI from '@/new-services/dispatcher-sectors.service'
import tmsAPI from '@/new-services/tms.service'
import { global_search } from '@/core/realisations/global_search.realisation'

// @composable
import { useNewDispatcherStore } from '@/modules/NewDispatcher/stores/useNewDispatcherStore'
import { useOnOutsidePress, useDebounce } from 'vue-composable'

import useNotifications from '@/common/composable/useNotifications'
import useAccount from '@/common/composable/useAccount'

const TMS_ROUTES_MATCHER = ['/contracts/bids', '/tms/']

export default defineComponent({
    props: {
        withoutSectors: {
            type: Boolean,
            default: false,
        },
    },
    components: {
        NewInput,
        NewSelect,
        // NotificationsBadge,
        HeaderSearchSuggestions,
        HeaderProfile,
        HeaderProfileDropdown,
        NewButton,
    },
    emits: ['complete-tms-day'],
    setup(props, { emit }) {
        const { havePermissions } = useAccount()
        const route = useRoute()
        const new_dispatcher_store = useNewDispatcherStore()
        const notifications = useNotifications()

        const state = reactive({
            is_header_dropdown_opened: false,
            is_custom_checked: false,
            is_in_tms_routes: false,
            withoutSectorId: null as number | null,
        })

        const complete_tms_day_state = reactive({
            is_loading: false,
            is_can_complete: false,
        })

        const elementref: Ref<HTMLElement | null> = ref(null)
        const searchref: Ref<HTMLElement | null> = ref(null)

        const search_state = reactive({
            is_loading: false,
            is_failed: false,
            query: null as string | null,
            suggestions: [] as GlobalSearchT[],
        })

        const data_state = reactive({
            dispatcher_sectors: {
                is_loading: false,
                is_failed: false,
                list: [] as DispatcherSector[],
            },
        })

        const loadDispatcherSectors = async () => {
            try {
                data_state.dispatcher_sectors.is_loading = true

                const response = await dispatcherSectorsAPI.getAllDispatcherSectors()
                data_state.dispatcher_sectors.list = response.data.data

                data_state.dispatcher_sectors.is_failed = false
            } catch (error) {
                data_state.dispatcher_sectors.is_failed = true
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При получении секторов диспетчера произошла ошибка',
                })
            } finally {
                data_state.dispatcher_sectors.is_loading = false
            }
        }

        const toggleHeaderDropdown = () => {
            state.is_header_dropdown_opened = !state.is_header_dropdown_opened
        }

        const closeHeaderDropdown = () => {
            state.is_header_dropdown_opened = false
        }

        const search = async () => {
            if (search_state.is_loading) return

            if (!search_state.query) {
                search_state.is_failed = false
                search_state.suggestions = []
                return
            }

            try {
                search_state.is_loading = true

                const response = await global_search.search(search_state.query)
                search_state.suggestions = response.data

                search_state.is_failed = false
            } catch (error) {
                search_state.is_failed = true
                console.error(error)
            } finally {
                search_state.is_loading = false
            }
        }

        const resetSearchState = () => {
            search_state.query = null
            search_state.suggestions = []
        }

        const searchSuggestions = useDebounce(search, 1000)

        useOnOutsidePress(elementref, closeHeaderDropdown)
        useOnOutsidePress(searchref, resetSearchState)

        onBeforeMount(() => {
            if (!props.withoutSectors) {
                loadDispatcherSectors()
                new_dispatcher_store.initialize()
                new_dispatcher_store.validation$.$touch()
            }

            checkInTmsRoutes()
        })

        const resetDispatcherSector = () => {
            new_dispatcher_store.dispatcherSectorIds = []
            new_dispatcher_store.dispatcherSectors = []
            new_dispatcher_store.syncWithLocalStorage()
        }

        const resetCustom = () => {
            new_dispatcher_store.isWithoutSectorId = false
            state.is_custom_checked = false
        }

        const loadCompleteDayStatus = async () => {
            try {
                complete_tms_day_state.is_loading = true

                const response = await tmsAPI.getCompleteDayStatus()
                complete_tms_day_state.is_can_complete = response.data.data.canCompleteDay
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При получении статуса завершения дня произошла ошибка',
                })
            } finally {
                complete_tms_day_state.is_loading = false
            }
        }

        const checkInTmsRoutes = () => {
            state.is_in_tms_routes = TMS_ROUTES_MATCHER.some((matcher) =>
                route.path.includes(matcher)
            )

            if (state.is_in_tms_routes && havePermissions(['tms.pre-trip-complete-day-status'])) {
                loadCompleteDayStatus()
            }
        }

        const onCompleteTmsDay = () => {
            emit('complete-tms-day', complete_tms_day_state.is_can_complete)
        }

        const onDispatcherSectorsChange = (dispatcher_sector_ids: number[]) => {
            resetCustom()

            if (!dispatcher_sector_ids || dispatcher_sector_ids.length === 0) {
                resetDispatcherSector()
                return
            }

            const dispatcher_sectors = data_state.dispatcher_sectors.list.filter(
                (dispatcher_sector) => dispatcher_sector_ids.includes(dispatcher_sector.id)
            )

            if (!dispatcher_sectors.length) {
                resetDispatcherSector()
                return
            }

            new_dispatcher_store.dispatcherSectorIds = [...dispatcher_sector_ids]
            new_dispatcher_store.dispatcherSectors = [...dispatcher_sectors]

            new_dispatcher_store.syncWithLocalStorage()
        }

        const onSelectCustom = (value: number) => {
            resetDispatcherSector()

            new_dispatcher_store.isWithoutSectorId = Boolean(value)
            state.is_custom_checked = true
        }

        const isNullableOption = computed(() => {
            return new_dispatcher_store.WITH_NULLABLE_ROUTES.includes(route.path)
        })
        const isCustomOption = computed(() => {
            return new_dispatcher_store.WITH_CUSTOM_ROUTES.includes(route.path)
        })
        const isSectorsVisible = computed(() => {
            return !new_dispatcher_store.WITHOUT_SECTORS.includes(route.path)
        })

        watch(() => route.path, checkInTmsRoutes)
        watch(
            () => props.withoutSectors,
            () => {
                if (!props.withoutSectors) loadDispatcherSectors()
            }
        )

        return {
            searchref,
            elementref,
            new_dispatcher_store,
            state,
            complete_tms_day_state,
            search_state,
            data_state,
            havePermissions,
            searchSuggestions,
            toggleHeaderDropdown,
            loadDispatcherSectors,
            onDispatcherSectorsChange,
            isNullableOption,
            isCustomOption,
            isSectorsVisible,
            onSelectCustom,
            onCompleteTmsDay,
        }
    },
})
